import { isKnownFinlandPlaceName } from './finland-place-names';
// name -> ISO code only, derived from scripts/data/geonames/countries.json (a test keeps them in
// sync): the full country records are 8x larger and this module is in every page's bundle.
import countryCodesByName from './country-codes-by-name.json';

/** country name (lowercased) -> ISO 3166-1 alpha-2 code (lowercased, Mapbox's expected format).
 * GeoNames spells some names with a leading article ("The Netherlands") that job text usually
 * drops ("Eemshaven, Netherlands"), so the article-less form is registered too. */
const COUNTRY_CODE_BY_NAME = new Map<string, string>(
  Object.entries(countryCodesByName as Record<string, string>).flatMap(([countryName, countryCode]) => {
    const name = countryName.toLowerCase();
    const code = countryCode.toLowerCase();
    return name.startsWith('the ') ? [[name, code], [name.slice(4), code]] : [[name, code]];
  }) as [string, string][],
);

/**
 * True when the last comma-separated segment names a real country other than Finland
 * ("Fredericia, Denmark"). The Finnish gazetteer carries historical and foreign-language aliases
 * that collide with real foreign cities: "Fredericia" is the old Latin name for Hamina
 * (Fredrikshamn), so without this guard every "Fredericia, Denmark" posting counted as Finnish.
 * An explicit foreign country beats a city-name match.
 */
function endsWithNonFinlandCountry(normalized: string): boolean {
  const segments = normalized.split(',').map((s) => s.trim()).filter(Boolean);
  if (segments.length < 2) return false;
  const code = COUNTRY_CODE_BY_NAME.get(segments[segments.length - 1]);
  return code !== undefined && code !== 'fi';
}

/** Unicode/en-dash style hyphens → ASCII so "Remote – Worldwide" parses like "Remote - Worldwide". */
const UNICODE_DASHES = /[\u2013\u2014\u2212\uFE58\uFE63\uFF0D]/g;

/**
 * @deprecated Superseded by the real GeoNames dataset (utils/map/finland-geonames-lookup.ts,
 * 910 places / 3,184 aliases), TEC-482. Kept exported rather than deleted since it's a public
 * symbol (nothing in this repo imports it as of the retirement, per a repo-wide grep, but an
 * exported symbol is a breaking change to remove outright). Bare-city detection below no longer
 * reads from this list.
 */
export const FINNISH_CITY_NAMES = [
  'helsinki', 'espoo', 'tampere', 'vantaa', 'oulu', 'turku', 'jyväskylä',
  'lahti', 'kuopio', 'pori', 'joensuu', 'lappeenranta', 'hämeenlinna',
  'vaasa', 'seinäjoki', 'rovaniemi', 'mikkeli', 'kotka', 'salo', 'porvoo',
  'kouvola', 'hyvinkää', 'nurmijärvi', 'järvenpää', 'rauma', 'tuusula',
  'kirkkonummi', 'kajaani', 'kerava', 'naantali', 'savonlinna', 'ylivieska',
  'valkeakoski', 'hamina', 'nokia', 'riihimäki', 'imatra', 'raahe',
  'helsingfors', 'tammerfors', 'esbo', 'vanda', 'åbo', // Swedish names
] as const;

/**
 * True if `candidate` (already lowercased/trimmed) is a known Finnish place name, exactly, 
 * checked against the real GeoNames dataset instead of a hand-typed list (TEC-482). Also used
 * by utils/location/enhance-location-filters.ts, which used to maintain its own separate,
 * narrower duplicate of this same list.
 */
function isBareFinnishCityName(candidate: string): boolean {
  return isKnownFinlandPlaceName(candidate);
}

/**
 * Minimum length for the trailing-word match below, real Finnish place names this short exist
 * in the gazetteer (e.g. "Ii", a real municipality), so a bare 2-letter trailing word is too
 * collision-prone to trust as a real match; every city name a real ATS board actually glues a
 * prefix onto ("Helsinki", "Oulu", "Turku", ...) clears this easily.
 */
const MIN_TRAILING_WORD_LENGTH = 4;

/**
 * True when the LAST whitespace-separated word of a multi-word segment exactly matches a known
 * Finnish place name, covers free-text glued in front of a real city name with no comma
 * separating them, which the segment-exact check above misses entirely. Confirmed live on two
 * independent patterns: a work-mode qualifier ("Hybrid - Helsinki", "On-site - Oulu", Oura's
 * Greenhouse board) and an office/company label ("Mapbox Helsinki", "Shook HQ Helsinki", both
 * real open Ashby postings). Restricted to 2+ word segments (a single-word segment is already
 * covered by the exact check) and a minimum trailing-word length to bound collision risk.
 */
function endsWithFinnishCityWord(segment: string): boolean {
  const words = segment.split(/\s+/).filter(Boolean);
  if (words.length < 2) return false;
  const lastWord = words[words.length - 1];
  return lastWord.length >= MIN_TRAILING_WORD_LENGTH && isBareFinnishCityName(lastWord);
}

/**
 * True when any comma/semicolon-separated segment of `normalized` (already lowercased) is
 * exactly a known Finnish city name, or ends with one, e.g. "helsinki", "helsinki, uusimaa",
 * "espoo; onsite", "hybrid - helsinki, uusimaa", "mapbox helsinki". Segment-exact rather than
 * blind substring matching, so an unrelated place that merely happens to contain a Finnish city
 * name as a substring isn't swept in (see the regression guards on "Salon, France" and
 * "Turkmenistan" below).
 */
function containsBareFinnishCitySegment(normalized: string): boolean {
  return normalized
    .split(/[,;]/)
    .some((segment) => {
      const trimmed = segment.trim();
      return isBareFinnishCityName(trimmed) || endsWithFinnishCityWord(trimmed);
    });
}

/**
 * Normalizes location text before Finland / remote-scope checks (dashes, spacing, "remote:").
 * Exported for unit tests.
 */
export function normalizeFinlandLocationInput(s: string): string {
  let t = s.replace(UNICODE_DASHES, '-');
  t = t.replace(/\s+/g, ' ').trim();
  t = t.replace(/^(remote|etätyö|etä|distans|fjärr)\s*:\s*/i, (_, p1: string) => `${p1.toLowerCase()} - `);
  return t;
}

/** First clause before comma / semicolon / paren / pipe, handles "Remote - Worldwide, EMEA". */
function primaryRemoteScopeSegment(tail: string): string {
  return tail.split(/[,;(|]/)[0].trim();
}

/**
 * Remote scopes that actually contain Finland. Regions that don't (North America, South America,
 * Asia, Africa, Oceania, MENA, Baltics, Benelux) used to be in this list too, so "Remote - North
 * America" counted as open to Finland and every such posting on an API-ingested board went to a
 * paid extraction call (confirmed 2026-10-04: Alpaca's and GitLab's North America-only postings).
 * A label naming several regions passes when any one of them is listed here
 * ("Remote - North America - EU - UK", "Remote - Europe or Remote America"); see
 * splitRegionalScopeParts.
 */
const REGIONAL_REMOTE_OPTIONS = [
  'global',
  'globaali',
  'globaalisti',
  'globalt',
  'worldwide',
  'world wide',
  'world',
  'europe',
  'eurooppa',
  'europa',
  'eu',
  'emea',
  'nordics',
  'nordic',
  'pohjoismaat',
  'scandinavia',
  'skandinavien',
] as const;

function matchesRegionalOptionsList(cand: string): boolean {
  return REGIONAL_REMOTE_OPTIONS.some((region) => cand === region || cand.startsWith(`${region} `));
}

/**
 * Splits a multi-region scope into its parts: "north america - eu - uk", "eu/latam/na",
 * "north america and remote - emea", "europe or remote america", "emea & apac". A leading
 * "remote" on a part is dropped so "remote america" reads as "america". Commas are NOT a list
 * separator here: "London, EMEA, United Kingdom" is a city/region/country hierarchy (confirmed on
 * SmartRecruiters' location object), and splitting it would let the "EMEA" part admit a
 * London-only posting.
 */
function splitRegionalScopeParts(tail: string): string[] {
  return tail
    .split(/\s+(?:-|and|or|&|\+)\s+|\s*[\/|]\s*/)
    .map((part) => part.replace(/^remote\s*-?\s*/, '').trim())
    .filter(Boolean);
}

/**
 * "Anywhere" is Finland-inclusive only when unqualified or qualified by a Finland-inclusive
 * scope: "Anywhere", "Work from anywhere", "Anywhere; Europe" (multi-location list), and
 * "Anywhere in the world / in Europe" all pass. A qualifier naming a narrower scope does NOT, 
 * "Anywhere in France" / "Anywhere in Latin America" are country/region-restricted postings
 * that merely use the word (both confirmed on live boards: Alan, Massive Rocket), and blanket-
 * matching the substring was tagging them isFinland=true.
 */
function anywhereScopeIncludesFinland(text: string): boolean {
  const qualified = text.match(/anywhere\s+(?:in|within|across)\s+(?:the\s+)?(.+)/i);
  if (!qualified) return true;
  const scope = qualified[1].trim();
  if (scope.includes('finland') || scope.includes('suomi')) return true;
  return matchesRegionalOptionsList(scope) || matchesRegionalOptionsList(primaryRemoteScopeSegment(scope));
}

function matchesRegionalRemoteScope(tail: string): boolean {
  const primary = primaryRemoteScopeSegment(tail);
  const candidates = [tail.trim(), primary];
  for (const cand of candidates) {
    if (matchesRegionalOptionsList(cand)) {
      return true;
    }
    if (/^anywhere\b/.test(cand)) {
      return anywhereScopeIncludesFinland(cand);
    }
  }
  const parts = splitRegionalScopeParts(tail);
  if (parts.length > 1 && parts.some((part) => matchesRegionalOptionsList(part) || part === 'finland' || part === 'suomi')) {
    return true;
  }
  const firstTwoWords = primary.split(/\s+/).slice(0, 2).join(' ');
  return firstTwoWords === 'world wide';
}

/**
 * Remote-style locations whose scope is broad enough to include Finland (regional list, empty suffix, standalone remote, "anywhere").
 * Does not return true for plain "Helsinki, Finland" unless it matches a remote pattern (callers combine with strict Finland checks as needed).
 */
/**
 * Strips the connective wrappers ATS boards put between "Remote" and its scope, so the scope
 * itself can be matched: "in EMEA" (Greenhouse's own "Remote in EMEA" location label, confirmed
 * on Testlio's board 2026-10-04, which had every EMEA/Global posting dropped by the API ingest's
 * Finland pre-filter), "within Europe", "(EMEA)".
 */
function stripRemoteScopeConnective(afterRemote: string): string {
  return afterRemote
    .replace(/^\(\s*(.*?)\s*\)$/, '$1')
    .replace(/^(?:in|within|across|from)\s+(?:the\s+)?/, '')
    .trim();
}

/**
 * Region labels that, standing alone as a whole location string (optionally tagged remote:
 * "Global", "Worldwide", "EMEA", "Europe (Remote)", "EU Remote"), describe a scope that includes
 * Finland. ATS boards use these bare, without a "Remote - " prefix: Greenhouse's "Global"
 * location label is the confirmed case (Testlio). Deliberately narrower than
 * REGIONAL_REMOTE_OPTIONS: only scopes that actually contain Finland.
 */
const FINLAND_INCLUSIVE_BARE_REGIONS = new Set([
  'global', 'globaali', 'globaalisti', 'globalt', 'worldwide', 'world wide',
  'europe', 'eurooppa', 'europa', 'eu', 'emea', 'nordics', 'nordic', 'pohjoismaat',
]);

function isFinlandInclusiveBareRegion(normalized: string): boolean {
  const withoutRemoteTag = normalized
    .replace(/\(\s*(?:remote|etätyö|distans)\s*\)/g, ' ')
    .replace(/\b(?:remote|fully remote)\b/g, ' ')
    .replace(/[\s,\-]+/g, ' ')
    .trim();
  return FINLAND_INCLUSIVE_BARE_REGIONS.has(withoutRemoteTag);
}

function isRemoteRegionalScopeFromNormalized(normalized: string): boolean {
  const dashRemoteMatch = normalized.match(/^(remote|etätyö|etä|distans|fjärr)\s*[-,]\s*(.*)$/i);
  const spaceRemoteMatch =
    !dashRemoteMatch && normalized.match(/^(remote|etätyö|etä|distans|fjärr)\s+(.+)$/i);

  let afterRemote: string | undefined;
  if (dashRemoteMatch) {
    afterRemote = stripRemoteScopeConnective(dashRemoteMatch[2].trim());
  } else if (spaceRemoteMatch) {
    afterRemote = stripRemoteScopeConnective(spaceRemoteMatch[2].trim());
  }

  if (afterRemote !== undefined) {
    if (afterRemote.includes('finland') || afterRemote.includes('suomi')) {
      return true;
    }
    if (matchesRegionalRemoteScope(afterRemote)) {
      return true;
    }
    if (afterRemote === '') {
      return true;
    }
    return false;
  }

  if (
    normalized === 'remote' ||
    normalized === 'etätyö' ||
    normalized === 'distans' ||
    normalized === 'fjärr'
  ) {
    return true;
  }

  if (normalized.includes('anywhere')) {
    return anywhereScopeIncludesFinland(normalized);
  }

  return isFinlandInclusiveBareRegion(normalized);
}

/** For job card location filtering, same remote-scope rules as {@link isFinlandLocation} minus explicit non-remote Finland strings. */
export function isRemoteRegionalScopeForJobCard(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  const normalized = normalizeFinlandLocationInput(location.toLowerCase().trim());
  return isRemoteRegionalScopeFromNormalized(normalized);
}

/**
 * Determines if a location qualifies as "Finland" for job filtering purposes.
 * 
 * A location qualifies as Finland if:
 * - It explicitly mentions Finland (e.g., "Helsinki, Finland", "Finland", "Suomi")
 * - It's a bare Finnish city name with no country suffix at all (e.g. "Helsinki"), confirmed
 *   live on Voodoo SAS's Ashby board, whose Helsinki posting's `location` field never says
 *   "Finland"
 * - It's "Remote - Finland" (or language equivalents)
 * - It's a regional remote option that includes Finland (e.g., "Remote - Global", "Remote - Europe", "Remote - EMEA")
 * - It's standalone "Remote" (without a specific country/region suffix)
 *
 * A location does NOT qualify if:
 * - It's "Remote - <specific country>" where the country is not Finland (e.g., "Remote - Estonia", "Remote - United States")
 * - It's "Remote - <region without Finland>" (e.g., "Remote - North America", "Remote - Asia", "Remote - MENA")
 *
 * @param location - The location string to check
 * @returns true if the location qualifies as Finland, false otherwise
 */
export function isFinlandLocation(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;

  const normalized = normalizeFinlandLocationInput(location.toLowerCase().trim());

  // Anything that explicitly mentions Finland
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    return true;
  }

  if (!endsWithNonFinlandCountry(normalized) && containsBareFinnishCitySegment(normalized)) {
    return true;
  }

  return isRemoteRegionalScopeFromNormalized(normalized);
}

/**
 * Determines if a job qualifies as "Finland" based on its location_en array.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array qualifies as Finland, false otherwise
 */
export function isFinlandJob(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocation(loc));
}

/**
 * Strict check: Only includes locations that explicitly mention "Finland"/"Suomi", or are a bare
 * Finnish city name. Excludes remote-global jobs like "Remote - Europe", "Remote - Global", etc.
 *
 * This is used for calculating Finland-wide salary and trend aggregates to ensure
 * they only include jobs explicitly in Finland, not remote jobs available globally.
 *
 * A bare city name (e.g. "Helsinki") is treated as explicit, not ambiguous, here: this function's
 * job is to separate a named, specific Finnish place from an ambiguous remote-region scope like
 * "Remote - Europe", a city name is exactly as unambiguous as the literal word "Finland" would
 * be, it just doesn't spell out the country. See `isFinlandLocation` for the non-strict twin.
 *
 * @param location - The location string to check
 * @returns true if the location explicitly mentions Finland/Suomi or a Finnish city, false otherwise
 */
export function isFinlandLocationStrict(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;

  const normalized = location.toLowerCase().trim();

  // Only include locations that explicitly mention Finland or Suomi
  // This includes:
  // - "Helsinki, Finland", "Tampere, Finland", etc.
  // - "Finland", "Suomi"
  // - "Remote - Finland", "Etätyö - Suomi" (remote jobs specifically for Finland)
  // - A bare Finnish city name with no country suffix (e.g. "Helsinki")
  //
  // This excludes:
  // - "Remote - Global", "Remote - Europe", "Remote - EMEA", etc.
  // - Standalone "Remote" without Finland mention

  // Check if it explicitly mentions Finland or Suomi
  if (normalized.includes('finland') || normalized.includes('suomi')) {
    // Make sure it's not "Remote - <something else>" that happens to contain "finland" in the wrong place
    // For example, we want "Remote - Finland" but not "Remote - Finland, Estonia" (though that's unlikely)
    return true;
  }

  if (!endsWithNonFinlandCountry(normalized) && containsBareFinnishCitySegment(normalized)) {
    return true;
  }

  return false;
}

/**
 * TEC-482 Tier 2: best-effort ISO 3166-1 alpha-2 country code for a raw job/company location
 * string, for restricting a Mapbox geocode query to the location's own actual country instead of
 * a blanket "search the whole planet" or the old hardcoded `country: 'fi'`. Same "last
 * comma-separated segment is the country" convention the gazetteer resolver
 * (utils/geocoding/location-resolver.ts) already uses, kept intentionally simple/synchronous
 * here rather than a full gazetteer resolve, see mapboxGeocodeFallbackForEnrichedJob.ts for why.
 *
 * Returns 'fi' for anything isFinlandLocationStrict already recognizes (bare Finnish city names
 * included, which have no country segment to parse at all). Returns undefined, never a guess, 
 * when the trailing segment doesn't match a real country name (a bare city with no country
 * context, or an unparseable string): callers should omit the `country` restriction entirely in
 * that case rather than passing a wrong one, since the query text itself may still carry enough
 * signal for Mapbox's own relevance ranking.
 */
export function resolveLocationCountryCode(location: string | undefined | null): string | undefined {
  if (!location || typeof location !== 'string') return undefined;
  if (isFinlandLocationStrict(location)) return 'fi';

  const segments = location.split(',').map((s) => s.trim()).filter(Boolean);
  if (segments.length === 0) return undefined;

  const candidate = segments[segments.length - 1].toLowerCase();
  return COUNTRY_CODE_BY_NAME.get(candidate);
}

/**
 * Strict check: Determines if a job qualifies as "Finland" based on its location_en array.
 * Only includes jobs where at least one location explicitly mentions "Finland" or "Suomi".
 * Excludes remote-global jobs.
 * 
 * @param job - Job object with location_en array
 * @returns true if any location in the array explicitly mentions Finland/Suomi, false otherwise
 */
export function isFinlandJobStrict(job: { location_en?: string[] }): boolean {
  if (!job.location_en || !Array.isArray(job.location_en)) return false;
  return job.location_en.some(loc => isFinlandLocationStrict(loc));
}

/**
 * If `location` is a bare Finnish city name with no country context at all (e.g. an ATS API
 * that just returns `"Helsinki"`, confirmed live on Voodoo SAS's Ashby board), appends
 * ", Finland" so the stored/displayed location actually says the country, not just the city.
 * Idempotent and conservative: already-qualified locations ("Helsinki, Finland", "Remote",
 * "Stockholm, Sweden") pass through unchanged. Intended for the ATS-API ingestion builders
 * (Ashby/Greenhouse/Workable/Lever) to call on each resolved location string before it's stored
 * as `location_en`/`location_{locale}`, those fields are applied as a deterministic overlay
 * *after* the LLM extraction step and are never otherwise corrected.
 *
 * @param location - The location string to normalize
 * @returns the location with ", Finland" appended if it was a bare Finnish city, else unchanged
 */
export function appendFinlandSuffixToBareCity(location: string): string {
  if (!location || typeof location !== 'string') return location;
  const trimmed = location.trim();
  if (!trimmed) return trimmed;

  const lower = trimmed.toLowerCase();
  if (lower.includes('finland') || lower.includes('suomi')) return trimmed;

  const normalizedForRemoteCheck = normalizeFinlandLocationInput(lower);
  if (isRemoteRegionalScopeFromNormalized(normalizedForRemoteCheck)) return trimmed;

  if (!containsBareFinnishCitySegment(lower)) return trimmed;

  return `${trimmed}, Finland`;
}
